// "Also remove the peaks this recording bagged?" — asked after a recording delete is
// confirmed, only when the recordings being deleted bagged something. The answer goes to
// the server as `removeBaggedPeaks`; Peaks::RecordingBagRemover does the removal.
//
// Same event name and properties as the iOS and Android apps, so one product question
// ("how often do people take their peaks with them?") reads the same on every platform.

export interface RecordingWithBaggedPeaks {
  id: string | number
  // Null for a viewer who doesn't own the recording, and absent on any cached row
  // fetched before the field existed — both read as "bagged nothing".
  baggedPeaksCount?: number | null
}

export const BAGGED_PEAKS_REMOVED_EVENT = 'Recording Bagged Peaks Removed'

// How many peaks the recordings with these ids bagged, summed. Ids are compared as
// strings because the list holds GraphQL `ID`s while selection code sometimes has numbers.
export function baggedPeaksCountFor(
  recordings: RecordingWithBaggedPeaks[],
  ids: Array<string | number>,
): number {
  const wanted = new Set(ids.map(String))
  return recordings
    .filter((recording) => wanted.has(String(recording.id)))
    .reduce((total, recording) => total + Math.max(0, recording.baggedPeaksCount ?? 0), 0)
}

// Only ask when there is something to remove — a question about peaks on a recording
// that bagged none is noise.
export function shouldAskToRemoveBaggedPeaks(baggedPeaksCount: number): boolean {
  return baggedPeaksCount > 0
}

export function baggedPeaksRemovedEventProperties(peakCount: number, recordingCount: number) {
  return {
    peak_count: peakCount,
    recording_count: recordingCount,
    bulk: recordingCount > 1,
  }
}
